import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { getHeightPriorToMenuOpen, isVerticallyScrollable } from "../utilities";
import ChevronLeftSvg from "./ChevronLeftSvg";
import MenuItem from "./MenuItem";
import debounce from "../../../utils/debounce";
import getRelativeScroll from "../../../utils/getRelativeScroll";
import clamp from "../../../utils/clamp";
import MenuItemBackButton from "./MenuItemBackButton";
import localizedData from "../../localizedData";

declare const wp;

const { __ } = wp.i18n;

// Scroll end threshold determines from which relative scroll limit
// the chevron indicators are getting rendered at top and bottom of a column.
const SCROLL_END_THRESHOLD = 5;

const MenuColumn = ( { column, activeColumn, handleSubmenuToggle } ) => {
	const getMaxMaskHeight = () => {
		const rootStyle = getComputedStyle( document.documentElement );
		const maxMaskHeight = rootStyle
			.getPropertyValue( '--mask-max-height' )
			.trim();
		return parseInt( maxMaskHeight );
	};
	const maxMaskHeight = getMaxMaskHeight();

	const scrollableContainer = useRef<HTMLUListElement>( null );
	const backButton = useRef<HTMLDivElement>( null );

	const [ maskHeightTop, _setMaskHeightTop ] = useState( 0 );
	const [ maskHeightBottom, _setMaskHeightBottom ] = useState( 0 );
	const setMaskHeightTop = ( value: number ): void => {
		_setMaskHeightTop( clamp( value, 0, maxMaskHeight ) );
	};
	const setMaskHeightBottom = ( value: number ): void => {
		_setMaskHeightBottom( clamp( value, 0, maxMaskHeight ) );
	};

	const [ scrollAtTop, setScrollAtTop ] = useState( true );
	const [ scrollAtBottom, setScrollAtBottom ] = useState( true );
	const [ currentPageItemRef, setCurrentPageItemRef ] = useState( null );
	const [ currentPageItemScrolledIntoView, setCurrentPageItemScrolledIntoView ] = useState( false );

	const handleResize = () => {
		const containerElement = scrollableContainer.current;
		const verticallyScrollable = containerElement ?
			isVerticallyScrollable( containerElement ) : false;
		if ( verticallyScrollable ) {
			const relativeScrolls = getRelativeScroll( containerElement );
			setScrollAtTop( relativeScrolls.top <= SCROLL_END_THRESHOLD );
			setScrollAtBottom( relativeScrolls.bottom <= SCROLL_END_THRESHOLD );
			setMaskHeightTop( relativeScrolls.top );
			setMaskHeightBottom( relativeScrolls.bottom );
		} else {
			setScrollAtTop( true );
			setScrollAtBottom( true );
			setMaskHeightTop( 0 );
			setMaskHeightBottom( 0 );
		}
	};

	const scrollCurrentPageItemIntoView = useCallback( () => {
		if ( ! scrollableContainer.current || ! currentPageItemRef ) return;

		const containerHeight = scrollableContainer.current?.clientHeight;
		const activeMenuTop = currentPageItemRef?.offsetTop;
		const activeMenuHeight = currentPageItemRef.clientHeight;
		const scrollPosition = activeMenuTop - ( containerHeight - activeMenuHeight ) / 2;

		scrollableContainer.current.scrollTo( {
			top: scrollPosition,
		} );

		setCurrentPageItemScrolledIntoView( true );
	}, [ currentPageItemRef ] );

	const openMenuHandler = ( e: CustomEvent ) => {
		if ( e.detail.dialogName !== 'menu' ) {
			return;
		}

		handleResize();
		scrollCurrentPageItemIntoView();
	};

	useEffect( () => {
		document.addEventListener( 'openDialog', openMenuHandler );

		return () => {
			document.removeEventListener( 'openDialog', openMenuHandler );
		};
	} );

	useEffect( () => {
		const nextSibling = scrollableContainer.current?.closest( 'div' )?.nextElementSibling as HTMLElement;
		const ulElement = nextSibling?.querySelector( ':scope > ul' ) as HTMLElement;

		if ( !ulElement ) return;

		const modifierClass = [ 'md:mt-[var(--backButtonHeight)]' ];

		if ( column.depth === activeColumn - 1 ) {
			const heightToAdd = getHeightPriorToMenuOpen( backButton.current ) ?? 0;
			ulElement.style.setProperty( '--backButtonHeight', `${heightToAdd}px` );
			ulElement.classList.add( ...modifierClass );
		} else {
			ulElement.classList.remove( ...modifierClass );
		}
	}, [ activeColumn, column.depth ] );

	const handleScroll = ( ev: React.UIEvent<HTMLElement> ) => {
		const relativeScrolls = getRelativeScroll( ev.target as HTMLElement );

		setScrollAtTop( relativeScrolls.top <= SCROLL_END_THRESHOLD );
		setScrollAtBottom( relativeScrolls.bottom <= SCROLL_END_THRESHOLD );

		setMaskHeightTop( relativeScrolls.top );
		setMaskHeightBottom( relativeScrolls.bottom );
	};

	useEffect( () => {
		const debouncedHandleResize = debounce( handleResize );

		window.addEventListener( 'resize', debouncedHandleResize );

		return () => {
			window.removeEventListener( 'resize', debouncedHandleResize );
		};
	} );

	useEffect( () => {
		handleResize();
	} );

	useEffect( () => {
		if ( currentPageItemScrolledIntoView ) return;
		scrollCurrentPageItemIntoView();
	}, [ currentPageItemRef, currentPageItemScrolledIntoView, scrollCurrentPageItemIntoView ] );

	const backButtonHiddenClasses = useCallback( () => {
		/**
		 * On tablet, if the active column is 0 OR if the activeColumn is the same as
		 * the one we're currently rendering, set the back button to invisible
		 */
		if ( activeColumn === 0 || column.depth === activeColumn ) {
			return 'md:hidden';
		}

		return '';
	}, [ activeColumn, column.depth ] );

	return (
		<div
			className={`
                menu-column flex-col h-full overflow-y-hidden flex shrink-0 justify-end md:justify-normal
                w-full md:w-[calc(50%-0.5rem)] xl:w-[calc(25%-0.75rem)]
            `}>

			<div
				ref={backButton}
				className={backButtonHiddenClasses()}
			>
				<MenuItemBackButton
					column={column}
					handleSubmenuToggle={handleSubmenuToggle}
				/>
			</div>

			<ChevronLeftSvg
				className={`
                    self-center w-[23px] h-[35px] rotate-90 top-0
					transition-opacity duration-300 ease-in-out absolute
					${scrollAtTop ? "opacity-0" : "opacity-100"}
                `}
			/>
			<ul
				ref={scrollableContainer}
				id={`menuLevel${column.depth + 1}`}
				style={{
					'--mask-height-top':`${maskHeightTop}px`,
					'--mask-height-bottom':`${maskHeightBottom}px`,
				} as unknown}
				className={`
                    md:h-full masked-overflow scrollbar-hide gap-[6px]
                    transition-[margin-top] duration-300 list-none m-0
                    relative ps-0
                `}
				onScroll={handleScroll}
			>

				{column.parentItem && column.hasOverviewItem && (
					<MenuItem
						depth={column.depth}
						key={column.parentItem.ID}
						activeID={column.activeID}
						ID={column.parentItem.ID}
						PageID={column.parentItem.object_id}
						url={column.parentItem.url}
						title={`${__( 'Overview', 'ufr-main-theme' )} ${column.parentItem.title}`}
						hasChildren={false}
						isOverview={true}
						isPartOfOpeningPath={localizedData.menuApiUriData.currentPageId === column.parentItem?.object_id}
						setCurrentPageItemRef={setCurrentPageItemRef}
					/>
				)}

				{column.items.map( ( item ) => {
					const isCurrentPage = localizedData.menuApiUriData.currentPageId === item.object_id;

					return (
						<MenuItem
							depth={column.depth}
							key={item.ID}
							activeID={column.activeID}
							ID={item.ID}
							PageID={item.object_id}
							url={item.url}
							title={item.title}
							columnHasOverviewItem={column.hasOverviewItem ?? false}
							handleSubmenuToggle={
								() => handleSubmenuToggle( column.depth, item.ID )
							}
							hasChildren={item.hasChildren}
							isPartOfOpeningPath={isCurrentPage || item.ID === column.activeID}
							setCurrentPageItemRef={setCurrentPageItemRef}
						/>
					);
				} ) }
			</ul>
			<ChevronLeftSvg
				className={`
					self-center w-[23px] h-[35px] -rotate-90 bottom-0
                    transition-opacity duration-300 ease-in-out absolute
                    ${scrollAtBottom ? "opacity-0" : "opacity-100"}
				`}
			/>
		</div>
	);
};

export default MenuColumn;
